/* eslint-disable no-console */
import { AdyenCheckout, PayPal } from "@adyen/adyen-web"
import { ContentFooter } from "@app/components/core/ContentFooter/ContentFooter"
import { ContentHeader } from "@app/components/core/ContentHeader/ContentHeader"
import RetryError from "@app/components/core/RetryError"
import { useCustomNavigation } from "@app/components/navigation/NavigationHandler"
import Entrances from "@app/components/order/Entrances/Entrances"
import { LinkList } from "@app/components/order/LinkList/LinkList"
import OrderContentBody from "@app/components/order/OrderContentBody/OrderContentBody"
import { ParkingTickets } from "@app/components/order/ParkingTickets/ParkingTickets"
import { PriceSummary } from "@app/components/order/PriceSummary/PriceSummary"
import Products from "@app/components/order/Products/Products"
import Thermencard from "@app/components/order/Thermencard/Thermencard"
import Vouchers from "@app/components/order/Vouchers/Vouchers"
import OrderSubPage from "@app/components/pageTypes/OrderSubPage/OrderSubPage"
import { OrderAnimationTypes } from "@app/components/routing/AnimatedOrderRoute/AnimatedOrderRoute"
import { useRaiseError } from "@app/store/actions/errorActions"
import { removePriceAction, setFormattedOrder } from "@app/store/actions/orderActions"
import { useGetLoungerTypes, useGetPaymentGroups, useGetProducts, useGetStrings, useGetUpsellArticles } from "@app/store/service/cmsService"
import { useFormatOrder, useGetCountries, useSaveOrder } from "@app/store/service/connectorService"
import { useGetPaymentMethods } from "@app/store/service/connectorService.jsx"
import { appStore } from "@app/store/store"
import { handleKeyboardSelect } from "@app/utils/accessibility"
import { AdyenRequests } from "@app/utils/AdyenUtils"
import { ADRESS_TYPES, IframeMessages, ORDER_LINE_TYPES } from "@app/utils/constants"
import { useDelayLoading } from "@app/utils/hooks/useDelayLoading"
import { getUrl } from "@app/utils/htmlUtils"
import { splitStreetToParts } from "@app/utils/stringUtils"
import tracking from "@app/utils/tracking"
import { faArrowCircleRight } from "@fortawesome/free-solid-svg-icons/faArrowCircleRight"
import { faCircleNotch } from "@fortawesome/free-solid-svg-icons/faCircleNotch"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { useViewport } from "@tecreation/funnel-components"
import { ResponseError, useSelector } from "@tecreation/js-state-react"
import { useCallback, useEffect, useRef, useState } from "react"

export default function OrderOverview() {
	const formattedOrder = useSelector(state => state.order.formattedOrder)
	const shoppingCart = useSelector(state => state.order.shoppingCart)
	const visitorKinds = useSelector(state => state.order.visitorKinds)
	const massages = useSelector(state => state.order.massages)
	const { data: countries } = useGetCountries()
	const webToken = useRef(null)
	const { push, pop, replace } = useCustomNavigation()
	const raiseError = useRaiseError()
	const ppRef = useRef({})
	const ppComp = useRef(null)
	const { sm } = useViewport()

	const [inprogress, setInProgress] = useState(false)

	const {
		data: { buttonCheckout, orderOverviewTitle, orderOverviewNotDone, orderOverviewContinueShopping },
	} = useGetStrings(undefined, { defaultData: {} })
	const [__formatOrder, { isFetching: __isFetching, error: formatError }] = useFormatOrder()
	const { data: loungerTypes } = useGetLoungerTypes({ locationId: appStore.getState().core.locationId })
	const { data: parkingTickets } = useGetUpsellArticles()
	const { data: baseProducts } = useGetProducts()
	const paypalInitProgress = useRef(false)
	const paypalDone = useRef(false)
	const payPalMethods = useRef(undefined)
	const relLoggedIn = useSelector(state => state.core.flow == "relation")
	const { data: payments } = useGetPaymentGroups()
	const { data: savedPaymentMethods } = useGetPaymentMethods({}, { skip: !relLoggedIn })
	const [saveOrder, __, abortOrder] = useSaveOrder()
	const htgGuest = appStore.get("core.htgGuestData")

	const [hasPositiveQuantity, setHasPositiveQuantity] = useState(false)

	const isFetching = useDelayLoading(__isFetching)

	const next = useCallback(() => {
		tracking.beginCheckout(formattedOrder)

		if (appStore.get("core.kioskMode")) {
			push("/order/next-order-overview", OrderAnimationTypes.fullSlide)
		} else {
			if (formattedOrder.payAmount <= 0) {
				if (appStore.get("core.invoiceAddress")) {
					push("/order/next-order-overview", OrderAnimationTypes.fullSlide)
				} else {
					push("/order/address-details", OrderAnimationTypes.fullSlide)
				}
			} else {
				appStore.set("order.payment", undefined)
				push("/order/payment-methods", OrderAnimationTypes.fullSlide)
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [formattedOrder])

	function uuidv4() {
		return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function (c) {
			const r = (Math.random() * 16) | 0,
				v = c == "x" ? r : (r & 0x3) | 0x8
			return v.toString(16)
		})
	}

	useEffect(() => {
		const execute = async () => {
			try {
				if (formattedOrder && payments && formattedOrder.orderLines.length > 0) {
					const paypalMethod = payments.find(e => e.type == "payPal")
					if (!paypalMethod) {
						return
					}
					let paypalComponent
					if (paypalDone.current == false && paypalInitProgress.current == false && formattedOrder.payAmount > 0) {
						paypalInitProgress.current = true
						AdyenRequests.init({ connectorKey: appStore.get("settings.connectorKey"), connectorUrl: "/api/connector" }, "")

						if (webToken.current == null && formattedOrder?.orderLines.length > 0) {
							webToken.current = uuidv4()
							appStore.set("order.payment", paypalMethod)
							await saveOrder({ payPalWebToken: webToken.current }).unwrap()
						}

						const methods = await AdyenRequests.getPaymentMethods({ webToken: webToken.current, allowedPaymentMethods: [] })
						const storedPaypalMethod = savedPaymentMethods?.find(e => methods.storedPaymentMethods?.find(f => f.type == "paypal" && f.shopperEmail == e.name))
						payPalMethods.current = methods
						paypalDone.current = true
						paypalInitProgress.current = false

						const clientKey = appStore.get("settings.adyenKey")
						const checkout = await AdyenCheckout({
							countryCode: "DE",
							environment: clientKey?.startsWith("test") ? "test" : "live",
							clientKey,
							paymentMethodsResponse: payPalMethods.current,

							onSubmit: async (state, _, actions) => {
								setInProgress(true)
								tracking.addPaymentInfo(formattedOrder, paypalMethod.type)
								try {
									appStore.set("order.payment", paypalMethod)
									await saveOrder({ payPalWebToken: webToken.current }).unwrap()
									appStore.set("order.payment", undefined)
									//generate custom data
									const data = {}

									// @ts-ignore
									const result = await AdyenRequests.pay({
										...state.data,
										...data,
										isReccuringPayment: storedPaypalMethod != null,
										relationPaymentToken: storedPaypalMethod?.relationPaymentToken,
										recurringProcessModel: 1,
										webToken: webToken.current,
										storePaymentMethod: false,
									})

									// If the /payments request from your server fails, or if an unexpected error occurs.
									if (!result.resultCode) {
										actions.reject()
										return
									}
									const { resultCode, action, order, donationToken } = result
									// If the /payments request request from your server is successful, you must call this to resolve whichever of the listed objects are available.
									// You must call this, even if the result of the payment is unsuccessful.

									// //start update order
									ppRef.current = result

									actions.resolve({
										resultCode,
										action,
										order,
										donationToken,
									})
								} catch (error) {
									console.error("onSubmit", error)
									appStore.set("order.payment", undefined)
									actions.reject()
									setInProgress(false)
								}
							},
							onAdditionalDetails: async (state, component, actions) => {
								try {
									// Make a POST /payments/details request from your server.
									const result = await AdyenRequests.getPaymentDetail({ webToken: webToken.current, data: state.data, relationPaymentToken: undefined })

									// If the /payments/details request from your server fails, or if an unexpected error occurs.
									if (!result.resultCode) {
										actions.reject()
										return
									}
									const { resultCode, action, order, donationToken } = result
									ppRef.current = result

									// If the /payments/details request from your server is successful, you must call this to resolve whichever of the listed objects are available.
									// You must call this, even if the result of the payment is unsuccessful.
									actions.resolve({
										resultCode,
										action,
										order,
										donationToken,
									})
								} catch (error) {
									setInProgress(false)
									console.error("onSubmit", error)
									actions.reject()
								}
							},
							onPaymentCompleted: () => {
								replace("/order-succeed?sucess=true&webToken=" + webToken.current + "&orderNumber=1")
							},
							onPaymentFailed: data => {
								setInProgress(false)
								replace("/order-failed?sucess=false&webToken=" + webToken.current + "&orderNumber=1")
								appStore.set("core.invoiceAddress", undefined)
								raiseError(data, undefined, false, true)
							},
							onOrderCancel: () => {
								setInProgress(false)
								appStore.set("core.invoiceAddress", undefined)
							},
							onError: error => {
								setInProgress(false)
								appStore.set("core.invoiceAddress", undefined)
								raiseError(error, undefined, false, true)
							},
						})
						paypalComponent = new PayPal(checkout, {
							amount: {
								currency: "EUR",
								value: Math.round(formattedOrder.payAmount * 100),
							},
							style: {
								height: 40,
								layout: sm ? "vertical" : "horizontal",
								shape: "rect",
								disableMaxWidth: true,
							},

							isExpress: true,
							onShippingAddressChange: async function (data, actions) {
								const countryId = countries?.find(e => e.countryCode.toLowerCase() == data.shippingAddress.countryCode?.toLowerCase())?.countryId ?? 60
								appStore.set("core.invoiceAddress", {
									countryCode: data.shippingAddress.countryCode,
									addressTypeId: ADRESS_TYPES.INVOICE,
									countryId,
									city: "",
									postalCode: data.shippingAddress.postalCode,
									address: "",
									zipCode: data.shippingAddress.postalCode,
								})

								const x = await formatOrder(loungerTypes, parkingTickets, baseProducts)
								if (x instanceof Error) {
									raiseError(x, undefined, false, true)
									return actions.reject("UNKOWN")
								}
								await AdyenRequests.updateOrder({
									amount: x.payAmount,
									// @ts-ignore<
									paymentData: ppRef.current.action.paymentData,
									webToken: webToken.current,
									// @ts-ignore
									pspReference: ppRef.current.pspReference,
								}).catch(e => {
									actions.reject("UNKOWN")
									console.error(e)
									raiseError(x, undefined, false, true)
								})
							},
							onAuthorized: async (data, actions) => {
								const bcid = countries?.find(e => e.countryCode.toLowerCase() == data.billingAddress.country?.toLowerCase())?.countryId ?? 60
								const dcid = countries?.find(e => e.countryCode.toLowerCase() == data.deliveryAddress.country?.toLowerCase())?.countryId ?? 60
								const addressParts = splitStreetToParts(data.billingAddress.street)
								const delParts = splitStreetToParts(data.deliveryAddress.street)
								appStore.set("order.payment", paypalMethod)
								saveOrder({
									payPalWebToken: webToken.current,
									billingAddress: {
										address: addressParts.street,
										houseNo:
											data.billingAddress.houseNumberOrName != "ZZ" ? data.billingAddress.houseNumberOrName : addressParts.number + (addressParts.letter || ""),
										save: false,
										countryId: bcid,
										email: data.authorizedEvent.payer.email_address,
										city: data.billingAddress.city,
										countryCode: data.billingAddress.country,
										firstName: data.authorizedEvent.payer?.name.given_name,
										lastName: data.authorizedEvent.payer?.name.surname,
										zipCode: data.billingAddress.postalCode,
									},
									deliveryAddress: data.deliveryAddress
										? {
												address: delParts.street,
												houseNo:
													data.deliveryAddress.houseNumberOrName != "ZZ" ? data.deliveryAddress.houseNumberOrName : delParts.number + (delParts.letter || ""),
												save: false,
												countryId: dcid,
												email: data.authorizedEvent.payer.email_address,
												city: data.deliveryAddress.city,
												countryCode: data.deliveryAddress.country,
												firstName: data.authorizedEvent.payer?.name.given_name,
												lastName: data.authorizedEvent.payer?.name.surname,
												zipCode: data.deliveryAddress.postalCode,
											}
										: undefined,
								})
									.unwrap()
									.then(e => {
										console.log(e)
										actions.resolve()
									})
									.catch(e => {
										setInProgress(false)
										console.error(e)
										actions.reject()
									})
							},
						})
					}

					if (document.getElementById("paypal-container")) {
						if (formattedOrder.payAmount > 0 && paypalComponent) {
							paypalComponent.mount("#paypal-container")
							ppComp.current = paypalComponent
						} else if (formattedOrder.payAmount <= 0) {
							console.log("paypal component not mounted due to zero amount")
							ppComp.current?.unmount()
						}
					}
				}
			} catch (e) {
				raiseError(e, undefined, false, true)
			}
		}
		try {
			execute()
		} catch (e) {
			raiseError(e, undefined, false, true)
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [formattedOrder, payments, replace, saveOrder, savedPaymentMethods])

	useEffect(() => {
		return () => {
			ppComp.current?.unmount()
			abortOrder && abortOrder()
			appStore.set("order.payment", undefined)
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	const formatOrder = useCallback(async (loungerTypes, parkingTickets, baseProducts) => {
		try {
			const rsp = await __formatOrder().unwrap()
			setFormattedOrder(rsp, loungerTypes, parkingTickets, baseProducts)
			const linesWithQuantity = rsp.orderLines.filter(e => e.quantity >= 0 && e.details[0].typeId !== ORDER_LINE_TYPES.VoucherDetail).length
			setHasPositiveQuantity(linesWithQuantity !== 0)
			return rsp
		} catch (e_1) {
			let replacements = {}
			setHasPositiveQuantity(false)
			if (e_1 instanceof ResponseError) {
				let msg = e_1.response?.data?.errorMessage
				if (msg && msg.startsWith("err.order.priceActionNotApplicable")) {
					replacements = {
						"${codes}": JSON.parse(msg.split(";")[1]).join(", "),
					}
					raiseError(e_1, replacements, false, false)
					return e_1
				}
			}
			raiseError(e_1, replacements, false, true)
			return e_1
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	const handleContinueShoppingPress = e => {
		e.preventDefault()
		window.parent.postMessage(IframeMessages.openUrl + ";" + (getUrl() + appStore.get("core.languageCode") + "/shop"), "*")
	}

	useEffect(() => {
		if (loungerTypes !== undefined && parkingTickets !== undefined && baseProducts !== undefined && shoppingCart !== undefined) {
			formatOrder(loungerTypes, parkingTickets, baseProducts)
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [loungerTypes, parkingTickets, baseProducts, massages, shoppingCart])

	useEffect(() => {
		appStore.set("order.payment", undefined)
	}, [])

	if (formatError && formatError.response?.data?.errorMessage === undefined) {
		return <RetryError show loading={__isFetching} onRetry={() => formatOrder(loungerTypes, parkingTickets, baseProducts)} />
	}

	return (
		<OrderSubPage>
			{(isFetching || inprogress) && (
				<div className="absolute h-100 w-100 z-9 flex-center" style={{ backgroundColor: "rgba(0,0,0,0.6)" }}>
					<FontAwesomeIcon icon={faCircleNotch} className="fa-spin fa-4x color-fg-white" />
				</div>
			)}
			<ContentHeader
				canGoBack
				colorStyle="white"
				animation={OrderAnimationTypes.fullSlide}
				customReturnFunction={() => {
					if (!visitorKinds || !Object.keys(visitorKinds).length) {
						push("/")
					} else {
						pop(-1, OrderAnimationTypes.fullSlide)
					}
				}}
			>
				{orderOverviewTitle}
			</ContentHeader>
			<OrderContentBody className="color-fg-primary flex flex-column h-100" routeStyle={{ height: "100%" }}>
				{formattedOrder && (
					<div className="mt-2">
						<Entrances formattedOrder={formattedOrder} onAction={() => formatOrder(loungerTypes, parkingTickets, baseProducts)} deletable />
						<ParkingTickets formattedOrder={formattedOrder} onAction={() => formatOrder(loungerTypes, parkingTickets, baseProducts)} />
						{!appStore.get("core.kioskMode") && (
							<>
								<Products formattedOrder={formattedOrder} isEditable onAction={() => formatOrder(loungerTypes, parkingTickets, baseProducts)} />
							</>
						)}

						<Thermencard
							formattedOrder={formattedOrder}
							isEditable
							onAction={() => {
								formatOrder(loungerTypes, parkingTickets, baseProducts)
							}}
						/>
						<Vouchers
							formattedOrder={formattedOrder}
							onAction={data => {
								formatOrder(loungerTypes, parkingTickets, baseProducts).then(e => {
									if (e instanceof Error) {
										if (data) {
											removePriceAction(data)
											//call a again a format after invalid priceAction
											formatOrder(loungerTypes, parkingTickets, baseProducts)
										}
									}
								})
							}}
						/>

						<PriceSummary formattedOrder={formattedOrder} showTransporterFromPrice />
					</div>
				)}

				{!appStore.get("core.kioskMode") && appStore.get("core.frameSource") !== "7d" && !htgGuest && (
					<h2 className="h-without-default-style mt-3 clickable">
						<a
							className="color-fg-primary clickable"
							style={{ textDecoration: "none" }}
							target="_blank"
							tabIndex={0}
							onClick={handleContinueShoppingPress}
							onKeyDown={e => handleKeyboardSelect(e, () => handleContinueShoppingPress(e), false)}
						>
							{orderOverviewNotDone} <FontAwesomeIcon icon={faArrowCircleRight} /> {orderOverviewContinueShopping}
						</a>
					</h2>
				)}

				{!appStore.get("core.kioskMode") && <LinkList />}
			</OrderContentBody>
			<ContentFooter
				roundPrice={false}
				price={undefined}
				priceAbsolute
				showPayPal
				buttonProps={{
					disabledReason: !hasPositiveQuantity ? "err.receipt.NoPositiveLines" : "",
					text: buttonCheckout,
					onNext: next,
					disabled: !hasPositiveQuantity || (formattedOrder ? formattedOrder.orderLines.length === 0 : undefined),
				}}
				process={5}
			/>
		</OrderSubPage>
	)
}
